import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { dayText, FUEL_KINDS, FUEL_LABELS, litresText, parseConsumption, parsePerDay, perDayText, settingsChanges, vehicleRate } from './fuel';
import { FINANCE_KEY, SETTINGS_RESOURCE, type FinanceSettings, type FuelKind, type FuelView } from './types';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);
const MANUAL: Record<FuelKind, 'manualPb95Grosze' | 'manualOnGrosze' | 'manualLpgGrosze'> = { pb95: 'manualPb95Grosze', on: 'manualOnGrosze', lpg: 'manualLpgGrosze' };
const at = (iso: string) => new Date(iso).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' });

type Vehicle = { fuel: FuelKind; consumption: string; extra: string; perDay: string };
type Draft = { truck: Vehicle; car: Vehicle; roundTrip: boolean; manual: Record<FuelKind, string> };
const ml = (v: number | null | undefined) => (v === null || v === undefined ? '' : litresText(v));
const zlOrEmpty = (v: number | null | undefined) => (v === null || v === undefined ? '' : plnToInput(v));
/** Review I2: a field hidden by the field rules is missing from `s`: an empty form value (it is never sent). */
const draftOf = (s: FinanceSettings): Draft => ({
  truck: { fuel: s.truckFuel ?? 'on', consumption: ml(s.truckConsumptionMlPerKm), extra: zlOrEmpty(s.truckExtraPerKmGrosze), perDay: perDayText(s.truckPerDayGrosze) },
  car: { fuel: s.carFuel ?? 'pb95', consumption: ml(s.carConsumptionMlPerKm), extra: zlOrEmpty(s.carExtraPerKmGrosze), perDay: perDayText(s.carPerDayGrosze) },
  roundTrip: s.roundTrip ?? true,
  manual: { pb95: zlOrEmpty(s.manualPb95Grosze), on: zlOrEmpty(s.manualOnGrosze), lpg: zlOrEmpty(s.manualLpgGrosze) },
});
const VEHICLE_KEYS = { truck: ['truckFuel', 'truckConsumptionMlPerKm', 'truckExtraPerKmGrosze'], car: ['carFuel', 'carConsumptionMlPerKm', 'carExtraPerKmGrosze'] } as const;
/** Owner 2026-10-24 10:22 / 16:15: „Stała kwota za wydarzenie” (once per event) — its own field rule (shown and sent only when visible, 7a). */
const PER_DAY_KEY = { truck: 'truckPerDayGrosze', car: 'carPerDayGrosze' } as const;
const price = (text: string): number | null => (text.trim() === '' ? null : parsePln(text));

/**
 * Finanse → Ustawienia → Transport (owner 2026-10-24 01:27): the current average fuel prices (fetched by the server,
 * „Odśwież teraz”, or typed by hand), and per vehicle kind its fuel, consumption and extra per km; the cost per km is
 * counted live and replaces the per km typed before (kept only while no consumption is entered).
 */
export function TransportSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: [...FINANCE_KEY, 'settings'], queryFn: () => api<FinanceSettings>('/api/finance/settings') });
  const fuel = useQuery({ queryKey: [...FINANCE_KEY, 'fuel'], queryFn: () => api<FuelView>('/api/finance/fuel') });
  const s = settings.data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [manualOn, setManualOn] = useState<Record<FuelKind, boolean>>({ pb95: false, on: false, lpg: false });
  const [local, setLocal] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!s) return;
    const d = draftOf(s);
    setDraft(d);
    setManualOn({ pb95: d.manual.pb95 !== '', on: d.manual.on !== '', lpg: d.manual.lpg !== '' });
  }, [s]);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<FinanceSettings>('/api/finance/settings', { method: 'PATCH', body: { ...body, version: s!.version } }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
    },
  });
  const refresh = useMutation({
    mutationFn: () => api<FuelView>('/api/finance/fuel/refresh', { method: 'POST' }),
    onSuccess: (view) => queryClient.setQueryData([...FINANCE_KEY, 'fuel'], view),
  });
  const editable = can(me, SETTINGS_RESOURCE, 'edit');

  /** The draft as the server's fields (only what changed); null with the reason shown. */
  function changes(): Record<string, unknown> | null {
    if (!s || !draft) return null;
    const next: Record<string, unknown> = { roundTrip: draft.roundTrip };
    try {
      for (const kind of ['truck', 'car'] as const) {
        next[`${kind}Fuel`] = draft[kind].fuel;
        next[`${kind}ConsumptionMlPerKm`] = parseConsumption(draft[kind].consumption);
        next[`${kind}ExtraPerKmGrosze`] = draft[kind].extra.trim() === '' ? 0 : parsePln(draft[kind].extra);
        next[PER_DAY_KEY[kind]] = parsePerDay(draft[kind].perDay);
      }
      for (const k of FUEL_KINDS) next[MANUAL[k]] = manualOn[k] ? price(draft.manual[k]) : null;
    } catch (err) {
      return (setLocal((err as Error).message), null);
    }
    setLocal('');
    setSaved(false);
    return settingsChanges(s, next);
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = changes();
    if (body && Object.keys(body).length > 0) save.mutate(body);
  }
  const clean = s ? draftOf(s) : null;
  const shownDraft = draft && { ...draft, manual: Object.fromEntries(FUEL_KINDS.map((k) => [k, manualOn[k] ? draft.manual[k] : ''])) };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: editable && !!clean && !!shownDraft && !sameValues(shownDraft, clean),
    save: async () => {
      const body = changes();
      if (!body) return false;
      if (Object.keys(body).length > 0) await save.mutateAsync(body);
      return true;
    },
  });

  const f = fuel.data;
  /** The price a vehicle is counted with as the form stands (a manual price being typed counts at once). */
  function effective(k: FuelKind): { effectiveGrosze: number | null; manual: boolean } {
    if (draft && manualOn[k]) {
      try {
        const typed = price(draft.manual[k]);
        if (typed !== null) return { effectiveGrosze: typed, manual: true };
      } catch {
        /* shown on save */
      }
    }
    return { effectiveGrosze: f?.prices[k].fetched?.grosze ?? null, manual: false };
  }
  /** Review I2: a vehicle (or a typed price) the role may not see is not shown at all. */
  const sees = (keys: readonly string[]) => !!s && keys.every((k) => k in s);
  function live(kind: 'truck' | 'car') {
    if (!draft || !s) return null;
    if (!sees([...VEHICLE_KEYS[kind], kind === 'truck' ? 'truckPerKmGrosze' : 'carPerKmGrosze'])) return f?.perKm[kind] ?? null;
    let mlPerKm: number | null;
    let extra: number;
    try {
      mlPerKm = parseConsumption(draft[kind].consumption);
      extra = draft[kind].extra.trim() === '' ? 0 : parsePln(draft[kind].extra);
    } catch {
      return null;
    }
    return vehicleRate({ fuel: draft[kind].fuel, mlPerKm, extraPerKmGrosze: extra, legacyPerKmGrosze: (kind === 'truck' ? s.truckPerKmGrosze : s.carPerKmGrosze) ?? 0, price: effective(draft[kind].fuel) });
  }
  const fetchedAny = f ? FUEL_KINDS.map((k) => f.prices[k].fetched).find((x) => x !== null) : null;
  const setVehicle = (kind: 'truck' | 'car', patch: Partial<Vehicle>) => setDraft((d) => d && { ...d, [kind]: { ...d[kind], ...patch } });

  return (
    <section aria-label="Ustawienia finansów" data-feature-id="finance.settings">
      <h2>Transport własny — koszt</h2>
      <p className="lede">
        Ile firmę kosztuje własne auto: za każdy km spalanie × aktualna cena paliwa + zużycie (opony, serwis), do tego stała kwota za wydarzenie (raz na całe wydarzenie, nie za każdy dzień). To nie jest cena dla klienta — ta jest w „Finanse → Cenniki → Transport”.
      </p>
      {(settings.error || fuel.error) && <p className="alert" role="alert">{errorText(settings.error ?? fuel.error, 'Nie udało się wczytać ustawień')}</p>}
      <div className="fin-fuel" aria-label="Ceny paliw">
        {f && fetchedAny ? (
          <p>
            Aktualna średnia cena: {FUEL_KINDS.map((k) => (f.prices[k].fetched ? `${FUEL_LABELS[k]} ${formatPln(f.prices[k].fetched!.grosze)}/l` : `${FUEL_LABELS[k]} —`)).join(' · ')}, z dnia {dayText(fetchedAny.priceDate)}, źródło: {fetchedAny.sourceLabel}.
          </p>
        ) : (
          f && <p>Nie pobrano jeszcze cen paliw. Serwer pobiera je codziennie rano (Biuletyn wychodzi raz w tygodniu); możesz też wpisać cenę ręcznie.</p>
        )}
        {f?.lastAttempt && !f.lastAttempt.ok && (
          <p className="alert" role="alert">
            Ostatnie pobranie ({at(f.lastAttempt.at)}) nie powiodło się: {f.lastAttempt.error}. {fetchedAny ? `Liczę według cen z dnia ${dayText(fetchedAny.priceDate)}.` : ''}
          </p>
        )}
        {refresh.data?.refreshed?.ok && <p role="status">Pobrano ceny paliw.</p>}
        {refresh.error && <p className="alert" role="alert">{errorText(refresh.error, 'Nie udało się odświeżyć')}</p>}
        {editable && (
          <button type="button" className="btn" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
            {refresh.isPending ? 'Pobieram…' : 'Odśwież teraz'}
          </button>
        )}
      </div>
      {s && draft && (
        <form ref={guardRef} className="form fin-settings" aria-label="Koszt transportu własnego" onSubmit={submit}>
          {save.error instanceof ApiError && save.error.status === 409 && (
            <ConflictNotice
              onReload={() => {
                save.reset();
                void settings.refetch();
              }}
            />
          )}
          <fieldset className="fin-manual-prices">
            <legend>Cena paliwa wpisana ręcznie</legend>
            {FUEL_KINDS.filter((k) => sees([MANUAL[k]])).map((k) => (
              <div key={k} className="fin-manual-price">
                <label className="check">
                  <input type="checkbox" checked={manualOn[k]} onChange={(e) => setManualOn((m) => ({ ...m, [k]: e.target.checked }))} disabled={!editable} /> Wpisz cenę ręcznie: {FUEL_LABELS[k]}
                </label>
                {manualOn[k] && (
                  <label className="field">
                    <span>Cena {FUEL_LABELS[k]} (zł/l)</span>
                    <NumberInput kind="money" value={draft.manual[k]} onValueChange={(v) => setDraft((d) => d && { ...d, manual: { ...d.manual, [k]: v } })} disabled={!editable} />
                  </label>
                )}
              </div>
            ))}
            <p className="hint">Cena wpisana ręcznie zastępuje pobraną, dopóki nie odznaczysz pola.</p>
          </fieldset>
          {(['car', 'truck'] as const).filter((kind) => sees(VEHICLE_KEYS[kind]) || sees([PER_DAY_KEY[kind]])).map((kind) => {
            const fuelFields = sees(VEHICLE_KEYS[kind]);
            const rate = fuelFields ? live(kind) : null;
            const title = kind === 'car' ? 'Auto osobowe' : 'Auto dostawcze / ciężarowe';
            return (
              <fieldset key={kind} aria-label={title}>
                <legend>{title}</legend>
                {fuelFields && (
                  <>
                    <label className="field">
                      <span>Paliwo</span>
                      <select value={draft[kind].fuel} onChange={(e) => setVehicle(kind, { fuel: e.target.value as FuelKind })} disabled={!editable}>
                        {FUEL_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {FUEL_LABELS[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Średnie spalanie (l/100 km)</span>
                      <NumberInput value={draft[kind].consumption} onValueChange={(v) => setVehicle(kind, { consumption: v })} disabled={!editable} placeholder="np. 9,5" />
                    </label>
                    <label className="field">
                      <span>Dodatkowo za km — opony, serwis (zł)</span>
                      <NumberInput kind="money" value={draft[kind].extra} onValueChange={(v) => setVehicle(kind, { extra: v })} disabled={!editable} />
                    </label>
                  </>
                )}
                {sees([PER_DAY_KEY[kind]]) && (
                  <label className="field">
                    <span>Stała kwota za wydarzenie (zł)</span>
                    <NumberInput kind="money" value={draft[kind].perDay} onValueChange={(v) => setVehicle(kind, { perDay: v })} disabled={!editable} placeholder="0,00" />
                  </label>
                )}
                {rate && (
                  <p className={rate.mode === 'brak-ceny' ? 'alert' : 'fin-per-km'} aria-label={`Koszt za km — ${title}`}>
                    Koszt za km: <strong>{formatPln(rate.perKmGrosze)}</strong> ({rate.basis})
                  </p>
                )}
              </fieldset>
            );
          })}
          <label className="check" hidden={!sees(['roundTrip'])}>
            <input type="checkbox" checked={draft.roundTrip} onChange={(e) => setDraft((d) => d && { ...d, roundTrip: e.target.checked })} disabled={!editable} /> Km tam i z powrotem
          </label>
          <p className="hint">
            Koszt pozycji transportu w kosztorysie = koszt za km × km (tam i z powrotem, jeśli zaznaczone) + stała kwota × dni wydarzenia, razy liczba aut — tak samo przy „Ryczałt” i „Wg km” (to zmienia tylko cenę dla
            klienta).
          </p>
          <p className="hint">Nowa cena paliwa zmienia koszt transportu w kosztorysach roboczych. Wersja wysłana albo zaakceptowana zostaje przy koszcie za km i stałej kwocie z dnia wysłania.</p>
          {(local || (save.error && !(save.error instanceof ApiError && save.error.status === 409))) && (
            <p className="alert" role="alert">
              {local || errorText(save.error, 'Nie udało się zapisać')}
            </p>
          )}
          {saved && <p role="status">Zapisano.</p>}
          {editable && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending}>
                Zapisz
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
